import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "src/components/layout/website/Layout";
import Button from "src/components/ui/Button";
import Alert from "src/components/ui/Alert";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as planActions from "src/redux/slices/planSlice";
import { Plan, PlanFeatures } from "src/types/plan.types";

const FEATURE_LABELS: Record<keyof PlanFeatures, string> = {
  totalUsers: "team members",
  totalProducts: "products",
  totalTransactions: "inventory transactions / mo",
  totalSaleOrders: "sale orders / mo",
  totalPurchaseOrders: "purchase orders / mo",
  totalSuppliers: "suppliers",
};

const formatPrice = (plan: Plan) => {
  if (!plan.price) return "Free";
  const suffix = plan.durationInDays === 30 ? "/ month" : `/ ${plan.durationInDays} days`;
  return `₹${plan.price.toLocaleString("en-IN")} ${suffix}`;
};

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: plans, loading, error } = useAppSelector(
    (state) => state.plan.publicPlansList
  );

  useEffect(() => {
    dispatch(planActions.getPublicPlanListRequest());
  }, [dispatch]);

  return (
    <Layout
      meta={{
        title: "Pricing — Invyntra",
        description:
          "Simple, usage-based pricing for Invyntra's inventory management platform. Start free, upgrade as you grow.",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-20 px-6 text-center">
        <h1 className="text-4xl font-bold text-indigo-600 mb-4">
          Pricing Plans
        </h1>
        <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
          We're in early access — pricing and limits below are current, but
          may evolve as we learn from the first businesses on the platform.
        </p>

        <div className="max-w-6xl mx-auto">
          {loading && <p className="text-gray-500">Loading plans…</p>}

          {error && (
            <div className="max-w-md mx-auto">
              <Alert type="error" message={error} />
            </div>
          )}

          {!loading && !error && plans && plans.length === 0 && (
            <p className="text-gray-500">
              Plans aren't published yet. Contact us for early access.
            </p>
          )}

          {!loading && !error && plans && plans.length > 0 && (
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((p) => (
                <div
                  key={p.planCode}
                  className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition flex flex-col"
                >
                  <h2 className="text-2xl font-semibold mb-2">{p.planName}</h2>
                  {p.description && (
                    <p className="text-gray-500 text-sm mb-4">
                      {p.description}
                    </p>
                  )}
                  <p className="text-indigo-600 text-xl font-bold mb-6">
                    {formatPrice(p)}
                  </p>
                  <ul className="text-gray-600 text-sm space-y-2 mb-8 text-left flex-1">
                    {p.features &&
                      (Object.keys(FEATURE_LABELS) as Array<keyof PlanFeatures>)
                        .filter((key) => p.features?.[key] !== undefined)
                        .map((key) => (
                          <li key={key}>
                            ✅ {p.features![key].toLocaleString("en-IN")}{" "}
                            {FEATURE_LABELS[key]}
                          </li>
                        ))}
                  </ul>

                  <Button onClick={() => navigate("/contact")}>
                    {p.price ? "Talk to Us" : "Start Free"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PricingPage;
